import React from 'react';
import { createRoot, Root } from 'react-dom/client';

import { DEVICE_DESKTOP, DEVICE_MOBILE } from '../constants/device';
import { REACT_APP_VERSION } from '../constants/envs';
import { FONT_FAMILY_KEY } from '../constants/localStorage';

import { WatchType } from '@/modules/common/types';
import { ConfigurationType } from '@/modules/common/types/configuration';

import { isMobileUserAgent } from '@/modules/common/helpers/userAgent';
import { getHashFromString } from '@/mui/helpers';

import { setCustomCssVariables, unmountAllTags } from './dom';
import { getContainerId } from './embedCodeParams';
import { extractFontFamilies, fetchFont } from './fonts';
import { setStoreParam } from './localStorage';
import logger from './logger';
import { GlobalIntersectionObserver, GlobalResizeObserver } from './observers';

export const RootContainers = (() => {
  const disconnectedCallBack = { current: undefined as (() => void) | undefined };

  let rootContainer: HTMLElement | null = null;

  let hiddenWrapperPlayerDOMContainer: Element | null = null;

  let appContainer: Element | null = null;
  let portalContainer: Element | null = null;

  let watchStylesRoot: Element | null = null;
  let stylesObserver: MutationObserver | null = null;

  let muiStylesRoot: Element | null = null;
  let playerStylesRoot: Element | null = null;

  return {
    render(ReactLayout: React.ReactNode): Root {
      const parentBlock = document.getElementById(getContainerId());

      if (!parentBlock) {
        throw new Error('Parent block not found');
      }

      const playerWrapTagName = 'watch-player-container';

      hiddenWrapperPlayerDOMContainer = document.querySelector(playerWrapTagName);

      if (!hiddenWrapperPlayerDOMContainer) {
        const styles = document.createElement('style');

        styles.innerHTML = `${playerWrapTagName}{position:fixed;top:-100%;left:-100%;visibility:hidden;z-index:-9999}`;

        hiddenWrapperPlayerDOMContainer = document.createElement(playerWrapTagName);

        document.head.appendChild(styles);
        document.body.appendChild(hiddenWrapperPlayerDOMContainer);
      }

      if (!customElements.get('watch-application')) {
        class WatchApplication extends HTMLElement {
          constructor() {
            super();
            this.attachShadow({ mode: 'open' });

            if (this.shadowRoot) {
              this.shadowRoot.innerHTML = `
<player-css></player-css>
<mui-css></mui-css>
<watch-css></watch-css>
<watch-body data-device="${isMobileUserAgent ? DEVICE_MOBILE : DEVICE_DESKTOP}"></watch-body>
<watch-portal data-device="${isMobileUserAgent ? DEVICE_MOBILE : DEVICE_DESKTOP}"></watch-portal>
`;
            }
          }

          // eslint-disable-next-line class-methods-use-this
          disconnectedCallback() {
            disconnectedCallBack.current?.();
          }
        }

        customElements.define('watch-application', WatchApplication);
      }

      parentBlock.innerHTML = `<watch-application translate="no" class="notranslate"></watch-application>`;
      parentBlock.style.minWidth = '0';

      rootContainer = parentBlock.querySelector('watch-application');

      if (!rootContainer?.shadowRoot) {
        throw new Error('shadowRoot missing');
      }

      rootContainer.setAttribute('dir', 'ltr');

      playerStylesRoot = rootContainer.shadowRoot.querySelector('player-css');
      muiStylesRoot = rootContainer.shadowRoot.querySelector('mui-css');
      watchStylesRoot = rootContainer.shadowRoot.querySelector('watch-css');
      appContainer = rootContainer.shadowRoot.querySelector('watch-body');
      portalContainer = rootContainer.shadowRoot.querySelector('watch-portal');

      if (!playerStylesRoot || !muiStylesRoot || !watchStylesRoot || !appContainer || !portalContainer) {
        throw new Error('Missing one or more shadow DOM containers');
      }

      const isOwnStyle = (el: Element): boolean => {
        if (el.tagName === 'LINK') {
          return (el as HTMLLinkElement).href?.includes(`/${REACT_APP_VERSION}/`);
        }

        if (el.tagName === 'STYLE') {
          return (
            (process.env.NODE_ENV === 'development' && el.hasAttribute('data-vite-dev-id')) ||
            el.hasAttribute('data-lrecss')
          );
        }

        return false;
      };

      const getKey = (el: Element): string => {
        let key = '';

        if (isOwnStyle(el)) {
          if (el.tagName === 'LINK') {
            key = getHashFromString((el as HTMLLinkElement).href).toString(36);
          } else if (el.tagName === 'STYLE') {
            key = getHashFromString(el.innerHTML.trim()).toString(36);
          }
        }

        return key;
      };

      const headCloneMap = new WeakMap<Element, Element>();

      const shouldSyncStyles = (mutations: MutationRecord[]): boolean =>
        mutations.some((mutation) => {
          if (mutation.type === 'childList') {
            const nodes = [...mutation.addedNodes, ...mutation.removedNodes];

            return (
              nodes.some((node) => node instanceof Element && isOwnStyle(node)) ||
              (mutation.target instanceof Element && mutation.target.tagName === 'STYLE' && isOwnStyle(mutation.target))
            );
          }

          if (mutation.type === 'attributes') {
            return (
              mutation.target instanceof Element &&
              mutation.target.tagName === 'STYLE' &&
              (isOwnStyle(mutation.target) ||
                (process.env.NODE_ENV === 'development' && mutation.attributeName === 'data-vite-dev-id') ||
                mutation.attributeName === 'data-lrecss')
            );
          }

          if (mutation.type === 'characterData') {
            const parent = (mutation.target as CharacterData).parentElement;

            return parent && parent.tagName === 'STYLE' && isOwnStyle(parent);
          }

          return false;
        });

      const syncCloneFromSource = (source: Element, clone: Element): void => {
        if (source.tagName === 'STYLE') {
          if (clone.textContent !== source.textContent) {
            // eslint-disable-next-line no-param-reassign
            clone.textContent = source.textContent;
          }
        } else if (source.tagName === 'LINK') {
          const sourceLink = source as HTMLLinkElement;
          const cloneLink = clone as HTMLLinkElement;

          if (cloneLink.href !== sourceLink.href) {
            cloneLink.href = sourceLink.href;
          }

          if (cloneLink.media !== sourceLink.media) {
            cloneLink.media = sourceLink.media;
          }

          if (cloneLink.rel !== sourceLink.rel) {
            cloneLink.rel = sourceLink.rel;
          }
        }
      };

      const reconcileGroup = (sources: Element[], target: Element): void => {
        const allowedKeys = new Set<string>();
        let anchor: ChildNode | null = target.firstChild;

        sources.forEach((source) => {
          const key = getKey(source);

          if (key) {
            allowedKeys.add(key);

            let clone = headCloneMap.get(source);

            if (!clone) {
              clone = source.cloneNode(true) as Element;
              headCloneMap.set(source, clone);
            } else {
              syncCloneFromSource(source, clone);
            }

            clone.setAttribute('data-content-hash', key);

            if (clone.parentNode !== target || clone !== anchor) {
              target.insertBefore(clone, anchor);
            }

            anchor = clone.nextSibling;
          }
        });

        Array.from(target.children).forEach((child) => {
          const key = child.getAttribute('data-content-hash');

          if (key && !allowedKeys.has(key)) {
            target.removeChild(child);
          }
        });
      };

      const checkAndAppendTo = (): void => {
        const watch: Element[] = [];
        const player: Element[] = [];

        Array.from(document.head.children)
          .filter((el) => ['LINK', 'STYLE'].includes(el.tagName))
          .forEach((el) => {
            if (isOwnStyle(el)) {
              (el.hasAttribute('data-lrecss') ? player : watch).push(el);
            }
          });

        reconcileGroup(watch, watchStylesRoot!);
        reconcileGroup(player, playerStylesRoot!);
      };

      let windowHeight: number | null = null;
      let rootWidth = rootContainer.clientWidth;

      const mainResizeFn = (): void => {
        const newHeight = window.innerHeight;
        rootWidth = rootContainer!.clientWidth;

        if (windowHeight !== newHeight) {
          windowHeight = newHeight;
          const { top } = rootContainer!.getBoundingClientRect();
          const delta = Math.floor(newHeight - window.scrollY - top);

          setCustomCssVariables(rootContainer!.shadowRoot!.host as HTMLElement, {
            '--app-height': `${Math.max(0, delta + 1)}px`,
          });
        }
      };

      let resizeTimer: ReturnType<typeof setTimeout> | null = null;

      const mainResizeObserverFn = ({ contentRect }: ResizeObserverEntry): void => {
        if (rootWidth !== contentRect.width) {
          rootWidth = contentRect.width;
          clearTimeout(resizeTimer!);
          resizeTimer = setTimeout(() => window.dispatchEvent(new Event('resize')), 0);
        }
      };

      window.addEventListener('resize', mainResizeFn);
      mainResizeFn();
      GlobalResizeObserver.observe(rootContainer, mainResizeObserverFn);

      let portalObserver: MutationObserver | null = null;

      if (process.env.NODE_ENV === 'development') {
        portalObserver = new MutationObserver((mutations) => {
          if (mutations.some((m) => m.type === 'childList') && portalContainer!.children.length > 5) {
            logger.warn(
              'Was rendered to match elements into the portalContainer',
              `size => ${portalContainer!.children.length}`,
              'Please check',
            );
          }
        });

        portalObserver.observe(portalContainer, { childList: true });
      }

      const reactRoot = createRoot(appContainer!);

      let stylesSyncFrame: number | null = null;

      disconnectedCallBack.current = () => {
        window.history.replaceState({}, '', `${window.location.pathname}${window.location.search}`);
        window.removeEventListener('resize', mainResizeFn);
        GlobalIntersectionObserver.disconnect();
        GlobalResizeObserver.disconnect();
        portalObserver?.disconnect();
        if (stylesSyncFrame !== null) {
          window.cancelAnimationFrame(stylesSyncFrame);
        }
        stylesObserver?.disconnect();
        unmountAllTags();
        reactRoot.unmount();
      };

      let pendingStylesSync = false;

      stylesObserver = new MutationObserver((mutations) => {
        if (shouldSyncStyles(mutations)) {
          pendingStylesSync = true;

          if (!stylesSyncFrame) {
            stylesSyncFrame = window.requestAnimationFrame(() => {
              stylesSyncFrame = null;

              if (pendingStylesSync) {
                pendingStylesSync = false;
                checkAndAppendTo();
              }
            });
          }
        }
      });

      checkAndAppendTo();

      stylesObserver.observe(document.head, {
        attributes: true,
        characterData: true,
        childList: true,
        subtree: true,
      });

      reactRoot.render(ReactLayout);

      return reactRoot;
    },

    getRootContainer(): HTMLElement {
      return rootContainer!;
    },

    getMUIContainer(): Element {
      return muiStylesRoot!;
    },

    getMUIPortalContainer(): Element {
      return portalContainer!;
    },

    getMainWidth(inPopup: boolean, popupSize: number): number {
      return inPopup ? Math.floor((window.innerWidth / 100) * popupSize) : rootContainer!.clientWidth;
    },

    updateCssVariables(config: Omit<WatchType, 'channels'>): void {
      const fonts = new Set<string>();
      fonts.add(config.font);

      if (config.configuration) {
        extractFontFamilies(config.configuration as ConfigurationType).forEach((f) => fonts.add(f));
      }

      setStoreParam(FONT_FAMILY_KEY, [...fonts].sort().join(';'));
      fetchFont();

      setCustomCssVariables(rootContainer!.shadowRoot!.host as HTMLElement, {
        '--sc-title-color': config.cTitleColor,
        '--sc-title-font-size': config.cTitleSize,
        '--layout-popup-size': `${config.popupSize}%`,
        '--sc-desc-color': config.cDescColor,
        '--sc-desc-font-size': config.cDescSize,
        '--sc-vid-title-color': config.cVidTitleColor,
        '--sc-vid-title-font-size': config.cVidTitleSize,
        '--sc-vid-info-color': config.cInfoColor,
        '--hub-offset-top': `${config.offsetTop}px`,
      });
    },
  };
})();
